<template>
  <aha-modal
    v-if="isShowingModal"
    @hide="hide()"
    :title="title"
  >
    <p
      v-if="!$store.state.presentation.hasExampleResponses || defaultModal"
    >
      You are about to delete all <u v-tooltip="resultsTooltip" class="cursor-pointer">results</u>
      from participants <b>in this presentation</b>.
      <span class="block pt-2">
        <aha-icon
          name="shield"
          maintainColor
          svgMaintainColor
          class="mb-1"
        />
        Your own questions and content will not be removed.</span>
    </p>
    <p v-else>
      <span>
        Remove all example votes, submissions and reactions from this template?
      </span>
      <span class="block pt-2">The questions and content will not be removed.</span>
    </p>
    <template #action>
      <aha-button
        variant="danger"
        :disabled="isClearingData"
        @click="handleClearData()"
        :submitting="isClearingData"
        has-margin-right
      >
        Reset
      </aha-button>
      <aha-link @click="hide()">
        <span class="font-bold">Cancel</span>
      </aha-link>
    </template>
  </aha-modal>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import PresentationApi from '@/api/presentation'
import Utils from '@/util'
import constants from '@/constant'

export default {
  props: {
    presentation: {
      type: Object,
      default: () => {},
    },
    clearDataHandler: {
      type: Function,
      default: null,
    },
    title: {
      type: String,
      default: 'Clear data confirmation',
    },
    defaultModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isClearingData: false,
      isShowingModal: false,
    }
  },
  computed: {
    ...mapState({
      folders: (state) => state.folders,
      currentUser: (state) => state.currentUser,
    }),
    resultsTooltip() {
      return '<p>Results include all participants’ <b> votes</b>, <b>submissions</b> and <b>reactions</b>.</p>'
    },
  },
  mounted() {
    this.isShowingModal = true
  },
  methods: {
    ...mapActions(['setErrorOnInternalServerAction', 'setPresentationsAction']),
    ...mapActions('presentationReport', ['clearPresentationReportAction']),
    ...mapActions('presentationChat', ['clearChatMessagesAction']),
    ...mapActions(['patchPresentationAction']),
    gaTrackEvent(label, value) {
      Utils.gaTrackEvent(
        constants.gaCategories.PRESENTATION_LIST,
        label,
        value,
      )
    },
    async handleClearData() {
      this.isClearingData = true

      if (this.clearDataHandler) {
        this.clearDataHandler()
      } else {
        try {
          await PresentationApi.clearPresentationData({
            id: this.presentation.id,
          })
          this.patchPresentationAction({
            hasResults: false,
          })
          Utils.showToastSuccess('Reset all results')

          // reset participants count to the state
          const newPresentations = this.$store.state.presentations
            .map((p) => {
              if (this.presentation.id === p.id) {
                return {
                  ...p,
                  participantsCount: 0,
                  onlineCount: 0,
                  numberOfHearts: 0,
                  numberOfLikes: 0,
                  numberOfLaughs: 0,
                  numberOfSads: 0,
                  numberOfWows: 0,
                  exportData: {
                    ...this.presentation.exportData,
                    reportUrl: null,
                    reportStatus: null,
                    reportUpdatedAt: null,
                  },
                }
              }

              return p
            })

          this.setPresentationsAction(newPresentations)
          this.clearPresentationReportAction()
          this.gaTrackEvent(constants.gaCategories.PRESENTATION_LIST, 'Presentation List > Clear data > Succeed', this.presentation.id)
        } catch (error) {
          this.setErrorOnInternalServerAction(`Cannot erase data on presentation: ${this.presentation.name}`)
          this.gaTrackEvent(constants.gaCategories.PRESENTATION_LIST, 'Presentation List > Clear data > Failed', this.presentation.id)
          throw error
        } finally {
          this.isClearingData = false
          this.hide()
        }
      }

      this.clearChatMessagesAction({
        uniqueAccessCode: this.presentation.uniqueAccessCode,
      })
    },
    hide() {
      this.isShowingModal = false
      setTimeout(() => {
        this.$emit('hide')
      }, 150)
    },
  },
}
</script>
